import React, { useState } from 'react';
import { __ } from './translations';
import {
    Building,
    FileText,
    Mail,
    Users,
    Download,
    PencilLine,
    UserPlus,
    UserMinus,
    Save,
    X
} from 'lucide-react';

interface CompanyData {
    companyId: number;
    companyName: string;
    city: string;
    postalCode: string;
    street: string;
    number: string;
    nip: string;
    companyEmail: string;
    recipientName?: string;
    recipientCity?: string;
    recipientPostalCode?: string;
    recipientStreet?: string;
    recipientNumber?: string;
    recipientNip?: string;
    recipientEmail?: string;
    assignedUsers?: Array<{
        email: string;
        displayName: string;
    }>;
}

interface DocumentData {
    statute?: { url: string };
    statuteText?: string;
    regulations?: { url: string };
    regulationsText?: string;
    resolution?: { url: string };
    resolutionText?: string;
}

// FormField component for consistency
const FormField: React.FC<{
    id: string;
    label: string;
    type?: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    required?: boolean;
    pattern?: string;
    placeholder?: string;
    className?: string;
}> = ({
    id,
    label,
    type = "text",
    value,
    onChange,
    required = false,
    pattern,
    placeholder,
    className
}) => (
        <div className="mb-4">
            <label
                htmlFor={id}
                className="block text-sm font-medium text-gray-700 mb-1"
            >
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
                type={type}
                id={id}
                value={value}
                onChange={onChange}
                required={required}
                pattern={pattern}
                placeholder={placeholder}
                className={`
                block w-full px-3 py-2 rounded-md border-gray-300 
                shadow-sm focus:border-blue-500 focus:ring-blue-500 
                sm:text-sm placeholder-gray-400
                ${className}
            `}
            />
        </div>
    );


interface CompanyInformationSectionProps {
    companyName: string;
    setCompanyName: (value: string) => void;
    city: string;
    setCity: (value: string) => void;
    postalCode: string;
    setPostalCode: (value: string) => void;
    street: string;
    setStreet: (value: string) => void;
    number: string;
    setNumber: (value: string) => void;
    nip: string;
    setNip: (value: string) => void;
    companyEmail: string;
    setCompanyEmail: (value: string) => void;
    userEmails: string[];
}

const CompanyInformationSection: React.FC<CompanyInformationSectionProps> = ({
    companyName,
    setCompanyName,
    city,
    setCity,
    postalCode,
    setPostalCode,
    street,
    setStreet,
    number,
    setNumber,
    nip,
    setNip,
    companyEmail,
    setCompanyEmail,
    userEmails,
}) => {
    return (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    {__('Company Information')}
                </h3>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <FormField
                            id="company-name"
                            label={__('Company Name')}
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            required
                            placeholder={__('Enter company name')}
                        />
                    </div>

                    <FormField
                        id="city"
                        label={__('City')}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        placeholder={__('Enter city name')}
                    />

                    <FormField
                        id="postal-code"
                        label={__('Postal Code')}
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        required
                        pattern="\d{2}-\d{3}"
                        placeholder="00-000"
                    />

                    <FormField
                        id="street"
                        label={__('Street')}
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        required
                        placeholder={__('Enter street name')}
                    />

                    <FormField
                        id="building-number"
                        label={__('Building Number')}
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        required
                        placeholder={__('Enter building number')}
                    />

                    <FormField
                        id="nip"
                        label={__('NIP')}
                        value={nip}
                        onChange={(e) => setNip(e.target.value)}
                        required
                        pattern="\d{10}"
                        placeholder="0000000000"
                    />

                    <div className="md:col-span-2">
                        <div className="mb-4">
                            <label
                                htmlFor="primary-email"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                {__('Primary Email')}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <select
                                id="primary-email"
                                value={companyEmail}
                                onChange={(e) => setCompanyEmail(e.target.value)}
                                className="block w-full px-3 py-2 rounded-md border border-gray-300 
                                             shadow-sm focus:border-blue-500 focus:ring-blue-500 
                                             sm:text-sm bg-white"
                                required
                            >
                                <option value="">{__('Select primary email')}</option>
                                {userEmails.filter(Boolean).map((email, index) => (
                                    <option key={index} value={email}>
                                        {email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Recipient Information Form Section
const RecipientInformationSection: React.FC<{
    hasRecipient: boolean;
    handleRecipientToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
    recipientName: string;
    setRecipientName: (value: string) => void;
    recipientCity: string;
    setRecipientCity: (value: string) => void;
    recipientPostalCode: string;
    setRecipientPostalCode: (value: string) => void;
    recipientStreet: string;
    setRecipientStreet: (value: string) => void;
    recipientNumber: string;
    setRecipientNumber: (value: string) => void;
    recipientNip: string;
    setRecipientNip: (value: string) => void;
    recipientEmail: string;
    setRecipientEmail: (value: string) => void;
}> = ({
    hasRecipient,
    handleRecipientToggle,
    recipientName,
    setRecipientName,
    recipientCity,
    setRecipientCity,
    recipientPostalCode,
    setRecipientPostalCode,
    recipientStreet,
    setRecipientStreet,
    recipientNumber,
    setRecipientNumber,
    recipientNip,
    setRecipientNip,
    recipientEmail,
    setRecipientEmail,
}) => {
        return (
            <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                            {__('Recipient Information')}
                        </h3>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="has-recipient"
                                checked={hasRecipient}
                                onChange={handleRecipientToggle}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                                htmlFor="has-recipient"
                                className="ml-2 text-sm text-gray-900"
                            >
                                {__('Different Recipient Information')}
                            </label>
                        </div>
                    </div>
                </div>

                {hasRecipient && (
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <FormField
                                    id="recipient-name"
                                    label={__('Recipient Name')}
                                    value={recipientName}
                                    onChange={(e) => setRecipientName(e.target.value)}
                                    required={hasRecipient}
                                    placeholder={__('Enter recipient name')}
                                />
                            </div>

                            <FormField
                                id="recipient-city"
                                label={__('City')}
                                value={recipientCity}
                                onChange={(e) => setRecipientCity(e.target.value)}
                                required={hasRecipient}
                                placeholder={__('Enter city name')}
                            />

                            <FormField
                                id="recipient-postal-code"
                                label={__('Postal Code')}
                                value={recipientPostalCode}
                                onChange={(e) => setRecipientPostalCode(e.target.value)}
                                required={hasRecipient}
                                pattern="\d{2}-\d{3}"
                                placeholder="00-000"
                            />

                            <FormField
                                id="recipient-street"
                                label={__('Street')}
                                value={recipientStreet}
                                onChange={(e) => setRecipientStreet(e.target.value)}
                                required={hasRecipient}
                                placeholder={__('Enter street name')}
                            />

                            <FormField
                                id="recipient-number"
                                label={__('Building Number')}
                                value={recipientNumber}
                                onChange={(e) => setRecipientNumber(e.target.value)}
                                required={hasRecipient}
                                placeholder={__('Enter building number')}
                            />

                            <FormField
                                id="recipient-nip"
                                label={__('NIP')}
                                value={recipientNip}
                                onChange={(e) => setRecipientNip(e.target.value)}
                                required={hasRecipient}
                                pattern="\d{10}"
                                placeholder="0000000000"
                            />

                            <div className="md:col-span-2">
                                <FormField
                                    id="recipient-email"
                                    label={__('Email')}
                                    type="email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    // required={hasRecipient}
                                    placeholder={__('Enter recipient email')}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };




// User Management Form Section
const UserManagementSection: React.FC<{
    userEmails: string[];
    handleUserEmailChange: (index: number, value: string) => void;
    handleRemoveUser: (index: number) => void;
}> = ({
    userEmails,
    handleUserEmailChange,
    handleRemoveUser
}) => (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900">
                    {__('Assigned Users')}
                </h3>
            </div>
            <div className="p-6 space-y-4">
                {userEmails.map((email, index) => (
                    <div key={index} className="flex items-center gap-4">
                        <FormField
                            id={`user-${index}`}
                            label={`${__('User Email')} ${index + 1}`}
                            type="email"
                            value={email}
                            onChange={(e) => handleUserEmailChange(index, e.target.value)}
                            placeholder={__('Enter user email')}
                            className="flex-1"
                        />
                        {email && (
                            <button
                                type="button"
                                onClick={() => handleRemoveUser(index)}
                                className="mt-6 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                {__('Remove')}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

const EditCompanyInfo: React.FC<{
    companyData: CompanyData;
    documentData: DocumentData;
    nonce: string;
    restURL: string;
}> = ({ companyData, documentData, nonce, restURL }) => {
    const { statute, statuteText, regulations, regulationsText, resolution, resolutionText } = documentData;

    const [isEditing, setIsEditing] = useState(false);
    const [hasRecipient, setHasRecipient] = useState(!!companyData.recipientName);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Company information states
    const [companyName, setCompanyName] = useState(companyData.companyName || '');
    const [city, setCity] = useState(companyData.city || '');
    const [postalCode, setPostalCode] = useState(companyData.postalCode || '');
    const [street, setStreet] = useState(companyData.street || '');
    const [number, setNumber] = useState(companyData.number || '');
    const [nip, setNip] = useState(companyData.nip || '');
    const [companyEmail, setCompanyEmail] = useState(companyData.companyEmail || '');

    // Recipient information states
    const [recipientName, setRecipientName] = useState(companyData.recipientName || '');
    const [recipientCity, setRecipientCity] = useState(companyData.recipientCity || '');
    const [recipientPostalCode, setRecipientPostalCode] = useState(companyData.recipientPostalCode || '');
    const [recipientStreet, setRecipientStreet] = useState(companyData.recipientStreet || '');
    const [recipientNumber, setRecipientNumber] = useState(companyData.recipientNumber || '');
    const [recipientNip, setRecipientNip] = useState(companyData.recipientNip || '');
    const [recipientEmail, setRecipientEmail] = useState(companyData.recipientEmail || '');

    // User management states
    const [userEmails, setUserEmails] = useState(
        companyData.assignedUsers ? companyData.assignedUsers.map(user => user.email) : ['', '', '']
    );
    const [removedUsers, setRemovedUsers] = useState([]);

    const handleUserEmailChange = (index, value) => {
        const newEmails = [...userEmails];
        newEmails[index] = value;
        setUserEmails(newEmails);
    };

    const handleRecipientToggle = (e) => {
        setHasRecipient(e.target.checked);
        if (!e.target.checked) {
            // Clear recipient fields when unchecked
            setRecipientName('');
            setRecipientCity('');
            setRecipientPostalCode('');
            setRecipientStreet('');
            setRecipientNumber('');
            setRecipientNip('');
            setRecipientEmail('');
        }
    };

    const handleRemoveUser = (index) => {
        const userToRemove = userEmails[index];
        if (userToRemove && window.confirm(`Are you sure you want to remove ${userToRemove} from the company?`)) {
            setRemovedUsers([...removedUsers, userToRemove]);
            const newEmails = [...userEmails];
            newEmails[index] = '';
            setUserEmails(newEmails);
            if (companyEmail === userToRemove) {
                setCompanyEmail('');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await fetch(`${restURL}board-member/v1/update-company`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify({
                    company_id: companyData.companyId,
                    company_data: {
                        companyName,
                        city,
                        postalCode,
                        street,
                        number,
                        nip,
                        companyEmail,
                        recipientName: hasRecipient ? recipientName : '',
                        recipientCity: hasRecipient ? recipientCity : '',
                        recipientPostalCode: hasRecipient ? recipientPostalCode : '',
                        recipientStreet: hasRecipient ? recipientStreet : '',
                        recipientNumber: hasRecipient ? recipientNumber : '',
                        recipientNip: hasRecipient ? recipientNip : '',
                        recipientEmail: hasRecipient ? recipientEmail : '',
                        assignedUsers: userEmails.filter(Boolean),
                    },
                    removed_users: removedUsers,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update company information');
            }

            const data = await response.json();
            if (data.success) {
                setSuccessMessage('Company information updated successfully!');
                setIsEditing(false);
            } else {
                setError(data.message || 'Error updating company information');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            console.error('Error:', error);
        }
    };

    if (!isEditing) {
        return (
            <div className="space-y-6">
                {/* Main Info Card */}
                {companyData.missing_nip && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                {__('Please update your company information with the NIP number.')}
                            </p>
                        </div>
                    </div>
                </div>
            )}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                <Building className="h-5 w-5 text-gray-500" />
                                {__('Company Information')}
                            </h2>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <PencilLine className="h-4 w-4 mr-2" />
                                {__('Edit')}
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                        {/* Company Details */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white rounded-lg border p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {companyData.companyName}
                                </h3>
                                <div className="space-y-3">
                                    <p className="text-sm text-gray-500">{__('Full Address:')}</p>
                                    <p className="text-gray-900">
                                        {companyData.street} {companyData.number}
                                        <br />
                                        {companyData.postalCode} {companyData.city}
                                    </p>
                                    <div className="pt-2">
                                        <p className="text-sm text-gray-500">{__('NIP:')}</p>
                                        <p className="text-gray-900">{companyData.nip}</p>
                                    </div>
                                    <div className="pt-2">
                                        <p className="text-sm text-gray-500">{__('Primary Email:')}</p>
                                        <p className="text-gray-900">{companyData.companyEmail}</p>
                                    </div>
                                </div>
                            </div>

                            {companyData.recipientName && (
                                <div className="bg-white rounded-lg border p-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        {__('Recipient Information')}
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Name:')}</p>
                                            <p className="text-gray-900">{companyData.recipientName}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Address:')}</p>
                                            <p className="text-gray-900">
                                                {companyData.recipientStreet} {companyData.recipientNumber}
                                                <br />
                                                {companyData.recipientPostalCode} {companyData.recipientCity}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('NIP:')}</p>
                                            <p className="text-gray-900">{companyData.recipientNip}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Email:')}</p>
                                            <p className="text-gray-900">{companyData.recipientEmail}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Documents Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-gray-500" />
                                    {__('Documents')}
                                </h3>
                                <div className="space-y-4">
                                    {Object.entries({
                                        'Statute': documentData.statute,
                                        'Regulations': documentData.regulations,
                                        'Resolution': documentData.resolution
                                    }).map(([type, doc]) => (
                                        <div key={type} className="p-3 bg-white rounded shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2">{__(type)}</h4>
                                            {doc ? (
                                                <a
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                >
                                                    <Download className="h-4 w-4 mr-1" />
                                                    {__(`Download ${type}`)}
                                                </a>
                                            ) : (
                                                <p className="text-sm text-gray-500">
                                                    {__(`No ${type.toLowerCase()} available`)}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Users Section */}
                            <div className="bg-white rounded-lg border p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Users className="h-5 w-5 text-gray-500" />
                                    {__('Assigned Users')}
                                </h3>
                                <div className="space-y-2">
                                    {companyData.assignedUsers?.map((user, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <Mail className="h-4 w-4 text-gray-400" />
                                            <span className="text-gray-900">{user.email}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Add error/success messages */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-green-700">{successMessage}</p>
                        </div>
                    </div>
                </div>
            )}
            <CompanyInformationSection
                companyName={companyName}
                setCompanyName={setCompanyName}
                city={city}
                setCity={setCity}
                postalCode={postalCode}
                setPostalCode={setPostalCode}
                street={street}
                setStreet={setStreet}
                number={number}
                setNumber={setNumber}
                nip={nip}
                setNip={setNip}
                companyEmail={companyEmail}
                setCompanyEmail={setCompanyEmail}
                userEmails={userEmails}
            />

            {/* Recipient Information Section */}
            <RecipientInformationSection
                hasRecipient={hasRecipient}
                handleRecipientToggle={handleRecipientToggle}
                recipientName={recipientName}
                setRecipientName={setRecipientName}
                recipientCity={recipientCity}
                setRecipientCity={setRecipientCity}
                recipientPostalCode={recipientPostalCode}
                setRecipientPostalCode={setRecipientPostalCode}
                recipientStreet={recipientStreet}
                setRecipientStreet={setRecipientStreet}
                recipientNumber={recipientNumber}
                setRecipientNumber={setRecipientNumber}
                recipientNip={recipientNip}
                setRecipientNip={setRecipientNip}
                recipientEmail={recipientEmail}
                setRecipientEmail={setRecipientEmail}
            />
            <div>

                <UserManagementSection
                    userEmails={userEmails}
                    handleUserEmailChange={handleUserEmailChange}
                    handleRemoveUser={handleRemoveUser}
                />

                <div className="flex justify-end space-x-3 pt-6 border-t">
                    <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <X className="h-4 w-4 mr-2" />
                        {__('Cancel')}
                    </button>
                    <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {__('Save Changes')}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default EditCompanyInfo;
